"use client";

import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { usePathname as useNextPathname } from "next/navigation";
import TFLink, { usePathname } from "@/Web/Components/TFLink/TFLink";
import { useDirection } from "@/utils/useDirection";
import config from "@/config.json";
import { DOMAIN_HUB } from "@/config/domains";

interface LayoutBreadcrumbItem {
  title: string;
  href?: string;
  isActive?: boolean;
}
import {
  HiOutlineBookOpen,
  HiOutlineDocumentText,
  HiOutlineVideoCamera,
  HiOutlineMicrophone,
  HiOutlineCube,
  HiOutlineCollection,
  HiOutlineCalendar,
} from "react-icons/hi";
import { IoGridSharp, IoHeadsetSharp } from "react-icons/io5";
import { RiQuestionMark } from "react-icons/ri";

// Компонент логотипа
const Logo = ({ text }: { text: string }) => {
  return (
    <div
      className="text-3xl font-bold"
      style={{ textShadow: "0px 0px 5px rgba(0, 0, 0, 0.2)" }}
    >
      {text}
    </div>
  );
};

// Компонент кнопки Hub
const HubButton = () => {
  const tNavbar = useTranslations("web.components.TFLayoutNavbar");

  return (
    <TFLink
      href={`//${DOMAIN_HUB}`}
      noLang={true}
      className="text-md font-bold border-2 border-black rounded-full px-4 py-1"
    >
      {tNavbar("goToHub")}
    </TFLink>
  );
};

// Основной компонент навбара
interface TFLayoutNavbarProps {
  breadcrumbs?: LayoutBreadcrumbItem[];
}

export default function TFLayoutNavbar({
  breadcrumbs = [],
}: TFLayoutNavbarProps) {
  const direction = useDirection();
  const isRTL = direction === "rtl";
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const fullPathname = useNextPathname();
  const router = useRouter();
  const t = useTranslations("global.language");

  const isHomePage = pathname === "/" || pathname.match(/^\/[a-z]{2}\/?$/);
  const showBreadcrumbs = breadcrumbs.length > 0 && !isHomePage;

  // Инициализируем isScrolled как false для совпадения SSR и клиента
  const [isScrolled, setIsScrolled] = useState(false);

  // Обработчик скролла
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Управление отступом контента через CSS классы
  // Обновляем класс только при изменении isScrolled для анимации при скролле
  useLayoutEffect(() => {
    if (!isHomePage) return; // Только для главной страницы меняем класс при скролле

    const pageContent = document.getElementById("page-content");
    if (pageContent) {
      if (isScrolled) {
        pageContent.classList.remove("pt-navbar-home");
        pageContent.classList.add("pt-navbar-home-scrolled");
      } else {
        pageContent.classList.remove("pt-navbar-home-scrolled");
        pageContent.classList.add("pt-navbar-home");
      }
    }
  }, [isHomePage, isScrolled]);

  // Языковые настройки
  const languages = config.websiteLanguages as string[];
  const languageMapping = config.websiteLanguagesMapping as Record<
    string,
    string
  >;
  const pathLocale = fullPathname?.split("/")[1];
  const currentLocale =
    pathLocale && languages.includes(pathLocale)
      ? pathLocale
      : config.defaultWebsiteLanguage || languages[0] || "en";
  const currentLanguageLabel = languageMapping[currentLocale] || currentLocale;
  const currentLanguageTranslated =
    t(currentLanguageLabel as any) || currentLanguageLabel;

  const handleLanguageChange = (locale: string) => {
    if (locale === currentLocale || !fullPathname) return;
    const pathParts = fullPathname.split("/");
    pathParts[1] = locale;
    router.push(pathParts.join("/"));
  };

  const tNavbar = useTranslations("web.components.TFLayoutNavbar");

  // Навигационные пункты
  const navItems = [
    { href: "/platform/hub/extensions", label: tNavbar("extensions") },
    { href: "/library", label: tNavbar("library") },
    { href: "/news", label: tNavbar("news") },
    { href: "/help", label: tNavbar("help") },
    { href: "/donations", label: tNavbar("donations") },
  ];

  // Быстрые ссылки
  const quickLinks = [
    {
      title: tNavbar("quickLinks.tiferetHub"),
      href: `//${DOMAIN_HUB}`,
      icon: IoGridSharp,
      bgColor: "bg-slate-100",
      hoverBgColor: "group-hover:bg-slate-200",
      iconColor: "text-slate-600",
      noLang: true,
    },
    {
      title: tNavbar("quickLinks.hubExtensions"),
      href: "/platform/hub/extensions",
      icon: HiOutlineCube,
      bgColor: "bg-cyan-100",
      hoverBgColor: "group-hover:bg-cyan-200",
      iconColor: "text-cyan-600",
      noLang: false,
    },
    {
      title: tNavbar("quickLinks.news"),
      href: "/news",
      icon: HiOutlineDocumentText,
      bgColor: "bg-blue-100",
      hoverBgColor: "group-hover:bg-blue-200",
      iconColor: "text-blue-600",
      noLang: false,
    },
    {
      title: tNavbar("quickLinks.articles"),
      href: "/articles",
      icon: HiOutlineCollection,
      bgColor: "bg-green-100",
      hoverBgColor: "group-hover:bg-green-200",
      iconColor: "text-green-600",
      noLang: false,
    },
    {
      title: tNavbar("quickLinks.library"),
      href: "/library",
      icon: HiOutlineBookOpen,
      bgColor: "bg-purple-100",
      hoverBgColor: "group-hover:bg-purple-200",
      iconColor: "text-purple-600",
      noLang: false,
    },
    {
      title: tNavbar("quickLinks.videos"),
      href: "/videos",
      icon: HiOutlineVideoCamera,
      bgColor: "bg-red-100",
      hoverBgColor: "group-hover:bg-red-200",
      iconColor: "text-red-600",
      noLang: false,
    },
    {
      title: tNavbar("quickLinks.podcasts"),
      href: "/podcasts",
      icon: IoHeadsetSharp,
      bgColor: "bg-orange-100",
      hoverBgColor: "group-hover:bg-orange-200",
      iconColor: "text-orange-600",
      noLang: false,
    },
    {
      title: tNavbar("quickLinks.liveRooms"),
      href: "/live-rooms",
      icon: HiOutlineMicrophone,
      bgColor: "bg-indigo-100",
      hoverBgColor: "group-hover:bg-indigo-200",
      iconColor: "text-indigo-600",
      noLang: false,
    },
    {
      title: tNavbar("quickLinks.events"),
      href: "/events",
      icon: HiOutlineCalendar,
      bgColor: "bg-pink-100",
      hoverBgColor: "group-hover:bg-pink-200",
      iconColor: "text-pink-600",
      noLang: false,
    },
    {
      title: tNavbar("quickLinks.faq"),
      href: "/help/faq",
      icon: RiQuestionMark,
      bgColor: "bg-yellow-100",
      hoverBgColor: "group-hover:bg-yellow-200",
      iconColor: "text-yellow-600",
      noLang: false,
    },
  ];

  // Размеры для иконок
  const iconSizes =
    isHomePage && !isScrolled
      ? {
          gap: "gap-1",
          link: "p-1.5",
          icon: "w-12 h-12",
          iconSize: "w-6 h-6",
        }
      : isHomePage && isScrolled
        ? {
            gap: "gap-0.5",
            link: "p-1",
            icon: "w-9 h-9",
            iconSize: "w-4 h-4",
          }
        : {
            gap: "gap-0",
            link: "p-0.5",
            icon: "w-7 h-7",
            iconSize: "w-3.5 h-3.5",
          };

  return (
    <>
      {/* Верхний navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
          isScrolled
            ? "bg-black/5 backdrop-blur-md shadow-md"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <TFLink href="/" className="flex items-center">
              <Logo text={tNavbar("logo")} />
            </TFLink>

            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <TFLink
                  key={item.href}
                  href={item.href}
                  className={`px-3 py-2 rounded-lg text-sm font-medium text-black/50 whitespace-nowrap ${
                    pathname === item.href ? "bg-black/10" : "hover:bg-black/5"
                  }`}
                >
                  {item.label}
                </TFLink>
              ))}
            </div>

            <HubButton />

            <button
              className={`md:hidden p-2 rounded-xl ${isScrolled ? "hover:bg-slate-100/50 text-slate-600" : "hover:bg-slate-100 text-slate-600"}`}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* QuickLinks с breadcrumbs */}
      <div
        className={`fixed top-16 left-0 right-0 z-40 transition-[background-color,backdrop-filter,height] duration-300 ${
          isScrolled ? "bg-black/5 backdrop-blur-md" : "bg-transparent"
        }`}
        style={{
          height:
            isHomePage && !isScrolled
              ? "92px"
              : isHomePage && isScrolled
                ? "60px"
                : "48px",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 h-full relative">
          <div
            className={`flex items-center h-full ${isHomePage ? "justify-center" : "justify-end"}`}
          >
            {/* Breadcrumbs слева/справа (только НЕ на главной) */}
            {showBreadcrumbs && (
              <div className="absolute start-4 top-0 h-full flex items-center gap-2">
                {/* Селектор языка */}
                <div
                  ref={languageDropdownRef}
                  className="relative flex items-center"
                  onMouseEnter={() => setIsLanguageOpen(true)}
                  onMouseLeave={() => setIsLanguageOpen(false)}
                >
                  <span
                    className={`${isLanguageOpen ? "bg-white" : "bg-black/5"} text-black/70 px-2 py-1 cursor-pointer flex items-center text-xs rounded-md ${isLanguageOpen ? "rounded-b-none" : ""}`}
                  >
                    {currentLanguageTranslated}
                  </span>

                  {isLanguageOpen && (
                    <div
                      className={`absolute top-full ${isRTL ? "right-0 rounded-b-md rounded-tl-md" : "left-0 rounded-b-md rounded-tr-md"} bg-white z-[70] min-w-[100px]`}
                    >
                      <ul
                        className={`flex ${isRTL ? "flex-row-reverse" : "flex-row"} p-1.5 gap-1`}
                      >
                        {languages
                          .filter((locale) => locale !== currentLocale)
                          .map((locale) => {
                            const languageLabel =
                              languageMapping[locale] || locale;
                            const languageTranslated =
                              t(languageLabel as any) || languageLabel;
                            return (
                              <li key={locale}>
                                <button
                                  onClick={() => handleLanguageChange(locale)}
                                  className={`px-2 py-1 text-xs hover:bg-black/10 hover:text-slate-700 rounded-md whitespace-nowrap ${isRTL ? "text-right" : ""}`}
                                >
                                  {languageTranslated}
                                </button>
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Breadcrumbs */}
                <ol className="flex items-center gap-1.5 text-xs text-black/70">
                  {breadcrumbs.map((item, index) => (
                    <React.Fragment key={index}>
                      {item.href && !item.isActive ? (
                        <li className="flex items-center">
                          <TFLink
                            href={item.href}
                            className="text-black/70 hover:text-black/70"
                          >
                            {item.title}
                          </TFLink>
                        </li>
                      ) : (
                        <li className="flex items-center text-black/70">
                          {item.title}
                        </li>
                      )}
                      {index < breadcrumbs.length - 1 && (
                        <li className="text-black/70 px-0">
                          {isRTL ? "\u005C" : "/"}
                        </li>
                      )}
                    </React.Fragment>
                  ))}
                </ol>
              </div>
            )}

            {/* QuickLinks иконки */}
            <div
              className={`flex items-center transition-[gap] duration-300 ${iconSizes.gap}`}
            >
              {quickLinks.map((link, index) => (
                <TFLink
                  key={index}
                  href={link.href}
                  noLang={link.noLang}
                  className={`relative flex items-center justify-center rounded-full hover:bg-black/5 group transition-[padding] duration-300 ${iconSizes.link} p-1`}
                >
                  <div
                    className={`rounded-full ${link.bgColor} ${isHomePage ? link.hoverBgColor : ""} flex items-center justify-center transition-[width,height] duration-300 ${iconSizes.icon}`}
                  >
                    <link.icon
                      className={`${link.iconColor} transition-[width,height] duration-300 ${iconSizes.iconSize}`}
                    />
                  </div>

                  {/* Tooltip с правильным z-index */}
                  <span className="absolute top-full mt-2 left-1/2 -translate-x-1/2 px-4 py-2 bg-white text-black text-sm font-medium rounded-full whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-[100] shadow">
                    {link.title}
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-white"></span>
                  </span>
                </TFLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
